// Venue-search VS1.4: the items at the venue (inventory). Rows, their lists,
// their checks and what is saved. Plain functions, so tests can check every
// value. Items, kinds, sizes and rules come from the field dictionary
// (src/taxonomy: venue field `inventory`, lists.inventoryItem.parts); the
// lists were approved by Braeden in the VS1.4 plan (VD-1, 2026-11-03).
import { dictionary, listOptions, regionOf } from '../../../taxonomy';

const { lists } = dictionary;
const inventoryField = dictionary.entities.venue.fields.find((field) => field.id === 'inventory');
const part = (id) => inventoryField.item.find((field) => field.id === id);

export const OTHER_MAX = part('item').rule.otherMaxLength;
export const NOTE_MAX = part('note').rule.maxLength;
export const MIN_COUNT = part('count').rule.min;

// A choice is a value ID, or {list: "other", other: "text"} (field dictionary types.choice).
export const choiceId = (choice) => (choice && typeof choice === 'object' ? choice.list : choice);
export const otherText = (choice) => (choice && typeof choice === 'object' ? choice.other || '' : '');
export const isOther = (choice) => choiceId(choice) === 'other';

const partsOf = (row) => lists.inventoryItem.parts[choiceId(row.item)] || {};

export const itemOptions = () => listOptions('inventoryItem');

export const kindOptions = (row) => {
  const { kind } = partsOf(row);
  return kind ? listOptions(kind) : [];
};

export const hasSize = (row) => !!partsOf(row).size;

// The sizes the form offers for this row: the region's sizes, and only those of
// the kind picked when the list has kinds (forKind). Kind not picked yet: none.
// Kind "Other": only "Other". Sizes with no millimeters in the region: never.
export const sizeOptions = (row, country) => {
  const { size } = partsOf(row);
  if (!size) return [];
  const { forKind } = lists[size];
  const kind = choiceId(row.kind);
  if (forKind && !kind) return [];
  const regionSizes = regionOf(country).sizes[size] || {};
  return listOptions(size).filter(({ id }) => {
    if (id === 'other') return true;
    if (!regionSizes[id] || kind === 'other') return false;
    return !forKind || forKind[id] === kind;
  });
};

// Letters a person sees: an emoji or an accented letter counts as 1.
export const letters = (text) => {
  const value = String(text || '');
  if (typeof Intl !== 'undefined' && Intl.Segmenter) return [...new Intl.Segmenter('en', { granularity: 'grapheme' }).segment(value)].length;
  return Array.from(value).length;
};

// Plain text, one line: pasted line breaks and tabs become 1 space.
export const cleanText = (text) =>
  String(text || '')
    .replace(/\s*[\r\n\t]+\s*/g, ' ')
    .trim();

// Rows sorted in list order, with the rows of one item kept together.
const order = (row) => lists.inventoryItem.values.indexOf(choiceId(row.item));
export const sortRows = (rows) =>
  rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => order(a.row) - order(b.row) || a.index - b.index)
    .map(({ row }) => row);

export const rowsOf = (rows) => (Array.isArray(rows) ? rows : []);
export const hasItem = (rows, item) => rowsOf(rows).some((row) => choiceId(row.item) === item);

// A new row for an item. "Other" starts as {list: "other", other: ""}.
export const newRow = (item) => ({ item: item === 'other' ? { list: 'other', other: '' } : item });

// Check an item: add its first row. Uncheck it: remove all its rows.
export const toggleItem = (rows, item, on) => {
  const current = rowsOf(rows);
  if (!on) return current.filter((row) => choiceId(row.item) !== item);
  return hasItem(current, item) ? current : sortRows([...current, newRow(item)]);
};

// "+ Add another kind" (Braeden, VS1.4 decision A): a new row under the item's last row.
export const addRow = (rows, item) => sortRows([...rowsOf(rows), newRow(item)]);

export const removeRow = (rows, index) => rowsOf(rows).filter((_, i) => i !== index);

// Change one part of a row. A new kind clears a size that no longer fits it.
export const updateRow = (rows, index, changes, country) =>
  rowsOf(rows).map((row, i) => {
    if (i !== index) return row;
    const next = { ...row, ...changes };
    Object.keys(next).forEach((key) => next[key] === undefined && delete next[key]);
    if ('kind' in changes && next.size !== undefined && !isOther(next.size)) {
      if (!sizeOptions(next, country).some(({ id }) => id === next.size)) delete next.size;
    }
    return next;
  });

const textProblem = (text, max) => {
  const clean = cleanText(text);
  if (!clean) return 'missing';
  return letters(clean) > max ? 'tooLong' : null;
};

// { "inventory.<row>.<part>": problem } for every row.
export const inventoryProblems = (rows) => {
  const problems = {};
  rowsOf(rows).forEach((row, index) => {
    const key = (id) => `inventory.${index}.${id}`;
    ['item', 'kind', 'size'].forEach((id) => {
      if (!isOther(row[id])) return;
      const problem = textProblem(otherText(row[id]), OTHER_MAX);
      if (problem) problems[key(id)] = problem;
    });
    if (!Number.isInteger(row.count) || row.count < MIN_COUNT) problems[key('count')] = 'missing';
    if (!listOptions('itemPricing').some(({ id }) => id === row.pricing)) problems[key('pricing')] = 'missing';
    if (letters(cleanText(row.note)) > NOTE_MAX) problems[key('note')] = 'tooLong';
  });
  return problems;
};

const cleanChoice = (choice) => (isOther(choice) ? { list: 'other', other: cleanText(otherText(choice)) } : choice);

// The rows to save, in the dictionary's shape. No rows: undefined (not saved).
export const savedInventory = (rows, country) => {
  const saved = sortRows(rowsOf(rows)).map((row) => {
    const out = { item: cleanChoice(row.item), count: row.count };
    const kinds = kindOptions(row).map(({ id }) => id);
    if (row.kind !== undefined && kinds.includes(choiceId(row.kind))) out.kind = cleanChoice(row.kind);
    if (row.size !== undefined && sizeOptions(row, country).some(({ id }) => id === choiceId(row.size))) out.size = cleanChoice(row.size);
    out.pricing = row.pricing;
    const note = cleanText(row.note);
    if (note) out.note = note;
    return out;
  });
  return saved.length ? saved : undefined;
};

// rentals.offered (D-0074): yes when any item costs extra.
export const rentalsOffered = (rows) => rowsOf(rows).some((row) => row.pricing === 'costsExtra');

// Old amenities filled from the items (Braeden, VS1.4 decision B): on when
// that item is in the list and marked Included.
export const hasIncluded = (rows, item) => rowsOf(rows).some((row) => choiceId(row.item) === item && row.pricing === 'included' && row.count > 0);

// An old venue with no items yet: the items its old checkboxes named, ticked,
// so the host adds only the count and Included or Costs extra.
export const rowsFromAmenities = (labels = []) => {
  const map = lists.amenity.inventoryItem;
  const items = Object.keys(map)
    .filter((amenity) => labels.includes(lists.amenity.legacy[amenity]))
    .map((amenity) => map[amenity]);
  return items.length ? sortRows(items.map(newRow)) : undefined;
};
